import React, { useState } from 'react';
import axios from 'axios';
import { Alert, Box, FormControlLabel, Paper, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('fi-FI') : '-');

/**
 * Staff panel for the purge of a terminated organization's data
 * (organizations/services/data_purge.py): when it happens, and a switch that blocks it.
 * Staff get an email 3 days before any purge; this switch is where they stop it.
 */
export default function DataPurgePanel({ org, onChanged }) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  const handleToggle = async (event) => {
    setSaving(true);
    setError(false);
    try {
      await axios.post(`/api/vadmin/organizations/${org.id}/data-purge-block/`, {
        blocked: event.target.checked,
      });
      await onChanged();
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  let status;
  if (org.data_purged_at) {
    status = t('vadmin.dataPurgeDone', { date: formatDate(org.data_purged_at) });
  } else if (org.deactivated_at) {
    status = t('vadmin.dataPurgeScheduled', {
      since: formatDate(org.deactivated_at),
      date: formatDate(org.data_purge_date),
    });
  } else {
    status = t('vadmin.dataPurgeActive');
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }} data-testid="data-purge-panel">
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
        {t('vadmin.dataPurgeTitle')}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(false)}>
          {t('vadmin.cancelSubFailed')}
        </Alert>
      )}
      <Alert
        severity={org.deactivated_at && !org.data_purged_at && !org.data_purge_blocked ? 'warning' : 'info'}
        sx={{ mb: 1.5 }}
      >
        {status}
        {org.data_purge_blocked && !org.data_purged_at && ` ${t('vadmin.dataPurgeBlocked')}`}
        {org.data_purge_warning_sent_at && !org.data_purged_at && (
          <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
            {t('vadmin.dataPurgeWarningSent', { date: formatDate(org.data_purge_warning_sent_at) })}
          </Box>
        )}
      </Alert>
      <FormControlLabel
        control={
          <Switch
            checked={!!org.data_purge_blocked}
            onChange={handleToggle}
            disabled={saving}
            data-testid="data-purge-block-toggle"
          />
        }
        label={t('vadmin.dataPurgeBlock')}
      />
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('vadmin.dataPurgeBlockHelp')}
      </Typography>
    </Paper>
  );
}
